/*  src/pages/CompleteSignupPage.tsx  */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import AuthCard from '../components/AuthCard.tsx';
import CurriculumFields from '../components/CurriculumFields.tsx';
import { supabase } from '../supabaseClient.ts';
import { namesFromAuthUser } from '../utils/googleAuth.ts';
import { STUDENT_HOME } from '../utils/handoff.ts';
import { findStudentId, provisionStudentAccount } from '../utils/provisionStudent.ts';

/**
 * Year + subjects (and a chance to fix the name Google sent) after OAuth.
 * The session already exists; this is the first moment we can create the
 * student row without inventing curriculum picks.
 */
const CompleteSignupPage: React.FC = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [yearId, setYearId] = useState('');
  const [subjectIds, setSubjectIds] = useState<string[]>([]);

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login', { replace: true });
        return;
      }
      if (await findStudentId(user.id)) {
        navigate(STUDENT_HOME, { replace: true });
        return;
      }
      const names = namesFromAuthUser(user);
      setFirstName(names.firstName);
      setLastName(names.lastName);
      setReady(true);
    })();
  }, [navigate]);

  const toggleSubject = (id: string) =>
    setSubjectIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );

  const handleYearChange = (nextYearId: string) => {
    setYearId(nextYearId);
    setSubjectIds([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your first and last name.');
      return;
    }
    if (!yearId) {
      setError('Please choose a year level.');
      return;
    }

    setLoading(true);

    const extras = {
      signup_role: 'student',
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      signup_year_id: yearId,
      signup_subject_ids: subjectIds,
    };

    const { error: metaErr } = await supabase.auth.updateUser({
      data: {
        ...extras,
        full_name: `${extras.first_name} ${extras.last_name}`,
        display_name: `${extras.first_name} ${extras.last_name}`,
      },
    });
    if (metaErr) {
      setError(metaErr.message);
      setLoading(false);
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setError('Your session expired. Please log in again.');
      setLoading(false);
      return;
    }

    try {
      const studentId = await provisionStudentAccount(user, extras);
      if (!studentId) {
        setError('Could not finish creating your account.');
        setLoading(false);
        return;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not finish creating your account.');
      setLoading(false);
      return;
    }

    navigate(STUDENT_HOME, { replace: true });
  };

  if (!ready) {
    return (
      <AuthCard title="Setting up your account…">
        <CircularProgress sx={{ alignSelf: 'center' }} />
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Finish creating your account" onSubmit={handleSubmit} maxWidth={480}>
      <Typography variant="body2" color="text.secondary" textAlign="center">
        Tell us your year and subjects so we can show the right practice questions.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="First name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          autoComplete="given-name"
          fullWidth
          required
        />
        <TextField
          label="Last name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          autoComplete="family-name"
          fullWidth
          required
        />
      </Stack>

      <CurriculumFields
        yearId={yearId}
        subjectIds={subjectIds}
        onYearChange={handleYearChange}
        onToggleSubject={toggleSubject}
        onError={setError}
      />

      {error && <Alert severity="error">{error}</Alert>}

      <Button
        type="submit"
        variant="contained"
        size="large"
        disabled={loading}
        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
      >
        {loading ? 'Saving…' : 'Continue'}
      </Button>
    </AuthCard>
  );
};

export default CompleteSignupPage;
